import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { adminAPI } from '../../services/api';
import { Plus, Edit, Link as LinkIcon, Unlink, Camera, Globe, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';

const CamerasPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const queryClient = useQueryClient();

  const { data: cameras, isLoading: camerasLoading } = useQuery('admin-cameras', adminAPI.getCameras);
  const { data: companies } = useQuery('companies', adminAPI.getCompanies);
  
  // Debug logging
  console.log('Cameras data:', cameras);
  console.log('Type of cameras:', typeof cameras);
  console.log('Is cameras array:', Array.isArray(cameras));

  const createMutation = useMutation(adminAPI.createCamera, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-cameras');
      setShowCreateModal(false);
      toast.success('Camera created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create camera');
    }
  });

  const updateMutation = useMutation(
    ({ id, data }) => adminAPI.updateCamera(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-cameras');
        setShowEditModal(false);
        setSelectedCamera(null);
        toast.success('Camera updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update camera');
      }
    }
  );

  const assignMutation = useMutation(
    ({ cameraId, companyId }) => adminAPI.assignCamera(cameraId, companyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-cameras');
        setShowAssignModal(false);
        setSelectedCamera(null);
        toast.success('Camera assigned successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to assign camera');
      }
    }
  );

  const unassignMutation = useMutation(adminAPI.unassignCamera, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-cameras');
      toast.success('Camera unassigned successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to unassign camera');
    }
  });

  const deleteMutation = useMutation(adminAPI.deleteCamera, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-cameras');
      toast.success('Camera deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete camera');
    }
  });

  const handleEdit = (camera) => {
    setSelectedCamera(camera);
    setShowEditModal(true);
  };

  const handleAssign = (camera) => {
    setSelectedCamera(camera);
    setShowAssignModal(true);
  };

  const handleUnassign = (camera) => {
    if (window.confirm(`Are you sure you want to unassign ${camera.name} from ${camera.company_name}?`)) {
      unassignMutation.mutate(camera.id);
    }
  };

  const handleDelete = (camera) => {
    if (window.confirm(`Are you sure you want to delete ${camera.name}? This action cannot be undone and will remove the camera from both the database and MediaMTX.`)) {
      deleteMutation.mutate(camera.id);
    }
  };

  if (camerasLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <LoadingSpinner className="h-64" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cameras</h1>
          <p className="mt-2 text-gray-600">Manage cameras and company assignments</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Camera
        </button>
      </div>

      {/* Cameras List */}
      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Camera
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                MediaMTX Path
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                SFTP Path
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Photos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(cameras?.data?.data || cameras?.data || cameras || []).map((camera) => (
              <tr key={camera.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Camera className="h-5 w-5 text-primary-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{camera.name}</div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <Globe className="h-3 w-3 mr-1" />
                        ID: {camera.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {camera.mediamtx_path ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {camera.mediamtx_path}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Not assigned
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {camera.sftp_path || <span className="text-gray-400 italic">Not set</span>}
                  </div>
                  <div className="text-sm text-gray-500">
                    For camera serial: {camera.sftp_path ? camera.sftp_path.split('/').pop() : 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {camera.company_name ? (
                    <div className="text-sm text-gray-900">{camera.company_name}</div>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Unassigned
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    camera.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {camera.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {camera.photo_count}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {format(new Date(camera.created_at), 'MMM d, yyyy')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => handleEdit(camera)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Edit camera"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {camera.company_id ? (
                      <button
                        onClick={() => handleUnassign(camera)}
                        className="text-red-600 hover:text-red-900"
                        title="Unassign camera"
                      >
                        <Unlink className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAssign(camera)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Assign camera"
                      >
                        <LinkIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(camera)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete camera"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Camera Modal */}
      {showCreateModal && (
        <CreateCameraModal
          companies={(companies?.data?.data || companies?.data || companies || [])}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isLoading}
        />
      )}

      {/* Edit Camera Modal */}
      {showEditModal && selectedCamera && (
        <EditCameraModal
          camera={selectedCamera}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCamera(null);
          }}
          onSubmit={(data) => {
            updateMutation.mutate({ id: selectedCamera.id, data });
          }}
          isLoading={updateMutation.isLoading}
        />
      )}

      {/* Assign Camera Modal */}
      {showAssignModal && selectedCamera && (
        <AssignCameraModal
          camera={selectedCamera}
          companies={(companies?.data?.data || companies?.data || companies || [])}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedCamera(null);
          }}
          onSubmit={(companyId) => {
            assignMutation.mutate({ cameraId: selectedCamera.id, companyId });
          }}
          isLoading={assignMutation.isLoading}
        />
      )}
    </div>
  );
};

const CreateCameraModal = ({ companies, onClose, onSubmit, isLoading }) => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const handleFormSubmit = (data) => {
    onSubmit(data);
    reset();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Camera</h3>
          
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div>
              <label className="form-label">Camera Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter camera name (e.g., Golden Gate Main View)"
                {...register('name', { required: 'Camera name is required' })}
              />
              {errors.name && <p className="form-error">{errors.name.message}</p>}
            </div>

            <div>
              <label className="form-label">MediaMTX Camera Path</label>
              <select
                className="form-input"
                {...register('mediamtx_path', { 
                  required: 'MediaMTX path is required'
                })}
              >
                <option value="">Select camera path...</option>
                <option value="camera_1">camera_1 (Europa Parem - 37.157.64.48)</option>
                <option value="camera_2">camera_2 (Golden Gate - 217.71.44.97)</option>
                <option value="camera_3">camera_3 (Europa Vasak - 217.71.44.122)</option>
                <option value="camera_4">camera_4 (City Box - 217.71.41.222)</option>
              </select>
              {errors.mediamtx_path && <p className="form-error">{errors.mediamtx_path.message}</p>}
              <p className="text-xs text-gray-500 mt-1">
                Maps to hardcoded camera streams in mediamtx.yml configuration
              </p>
            </div>

            <div>
              <label className="form-label">SFTP Path (Optional)</label>
              <input
                type="text"
                className="form-input"
                placeholder="/camera_uploads/AH05C7DPAGEF482"
                {...register('sftp_path')}
              />
              <p className="text-xs text-gray-500 mt-1">
                Path on SFTP server where this camera uploads photos
              </p>
            </div>

            <div>
              <label className="form-label">Assign to Company (Optional)</label>
              <select className="form-input" {...register('company_id')}>
                <option value="">Select a company...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 btn btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 btn btn-primary flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Camera'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const EditCameraModal = ({ camera, onClose, onSubmit, isLoading }) => {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();

  // Set default values when modal opens
  React.useEffect(() => {
    if (camera) {
      setValue('name', camera.name);
      setValue('mediamtx_path', camera.mediamtx_path);
      setValue('sftp_path', camera.sftp_path);
      setValue('is_active', camera.is_active);
    }
  }, [camera, setValue]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Edit Camera: {camera.name}
          </h3>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="form-label">Camera Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter camera name"
                {...register('name', { required: 'Camera name is required' })}
              />
              {errors.name && <p className="form-error">{errors.name.message}</p>}
            </div>

            <div>
              <label className="form-label">MediaMTX Camera Path</label>
              <select
                className="form-input"
                {...register('mediamtx_path', { 
                  required: 'MediaMTX path is required'
                })}
              >
                <option value="">Select camera path...</option>
                <option value="camera_1">camera_1 (Europa Parem - 37.157.64.48)</option>
                <option value="camera_2">camera_2 (Golden Gate - 217.71.44.97)</option>
                <option value="camera_3">camera_3 (Europa Vasak - 217.71.44.122)</option>
                <option value="camera_4">camera_4 (City Box - 217.71.41.222)</option>
              </select>
              {errors.mediamtx_path && <p className="form-error">{errors.mediamtx_path.message}</p>}
              <p className="text-xs text-gray-500 mt-1">
                Maps to hardcoded camera streams in mediamtx.yml configuration
              </p>
            </div>

            <div>
              <label className="form-label">Camera ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="AH05C7DPAGC27F1"
                {...register('sftp_path')}
              />
              <p className="text-xs text-gray-500 mt-1">
                Camera serial/ID only (the system will automatically construct the full path: /upload/CAMERA_ID)
              </p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox mr-2"
                  {...register('is_active')}
                />
                <span className="form-label">Active</span>
              </label>
            </div>

            <div className="flex space-x-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 btn btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 btn btn-primary flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Updating...
                  </>
                ) : (
                  'Update Camera'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const AssignCameraModal = ({ camera, companies, onClose, onSubmit, isLoading }) => {
  const { register, handleSubmit, formState: { errors } } = useForm();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Assign Camera: {camera.name}
          </h3>
          
          <form onSubmit={handleSubmit((data) => onSubmit(data.company_id))} className="space-y-4">
            <div>
              <label className="form-label">Select Company</label>
              <select 
                className="form-input" 
                {...register('company_id', { required: 'Please select a company' })}
              >
                <option value="">Choose a company...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              {errors.company_id && <p className="form-error">{errors.company_id.message}</p>}
            </div>

            <div className="flex space-x-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 btn btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 btn btn-primary flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Assigning...
                  </>
                ) : (
                  'Assign Camera'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CamerasPage;

